import React, { useEffect, useState } from 'react';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { useAgentEdit } from '@/app/agents/edit/state/agentEditContext';
import { getNodeTypeLabel } from '@/app/agents/edit/util/nodeTypeUtils';
import { Menu, MenuItem } from '@/components/dropdownMenu/dropdownMenu';
import { Icon } from '@/components/icon/icon';
import { InputWithTemplateVars, TemplateVarItem } from '@/components/inputWithTemplateVars/inputWithTemplateVars';
import OptionRow from '@/components/optionRow/optionRow';
import { Tooltip } from '@/components/tooltip/tooltip';
import { Text } from '@/components/typography/text';
import { useAgentsQuery } from '@/hooks/queries/useAgents';
import {
  AgentConfigStepAgent,
  AgentConfigStepInputs,
  AgentConfigStepTypeEnum,
  AgentLowcode,
  AgentTypeEnum
} from '@/types/agent';

type Props = {
  agentAction: AgentConfigStepAgent;
  editable: boolean;
  inputs: AgentConfigStepInputs;
  onActionUpdate: <T extends AgentConfigStepAgent>( action: T ) => void;
  stepId: string;
};

const MESSAGES_INCLUDE_OPTIONS = {
  ALL: 'All messages',
  LAST: 'Last message',
  PLACEHOLDER: 'Messages to include'
} as const;

const AGENT_TYPE_OPTIONS: {
  display: string;
  testId: string;
  value: AgentTypeEnum;
}[] = [
  {
    display: 'coded',
    testId: 'agent-type-coded',
    value: AgentTypeEnum.AIDP
  },
  {
    display: 'lowcode',
    testId: 'agent-type-lowcode',
    value: AgentTypeEnum.LOW_CODE
  }
];

export default function ActionAgent( props: Props ) {
  const { agentAction, editable, inputs, onActionUpdate, stepId } = props;

  const { config, validationErrors } = useAgentEdit();
  const globalContext = config?.context || {};
  const contextValuesError = validationErrors[stepId]?.action?.context_values;

  const [ action, setAction ] = useState<AgentConfigStepAgent>( agentAction );
  const [ agentType, setAgentType ] = useState<AgentTypeEnum>(
    agentAction.agent_type ?? AgentTypeEnum.AIDP
  );

  const { isPending, error, data: agents } = useAgentsQuery( { enabled: true } );

  useEffect( () => {
    if ( error ) {
      console.error( `unable to get agents: ${ error }` );
      toast.error( 'Unable to load agents list' );
    }
  }, [ error ] );

  useEffect( () => {
    setAction( agentAction );
    setAgentType( agentAction.agent_type ?? AgentTypeEnum.AIDP );
  }, [ agentAction ] );

  const updateAction = ( updates: Partial<AgentConfigStepAgent> ) => {
    const res = { ...action, ...updates };
    setAction( res );
    onActionUpdate( res );
  };

  const onTypeChange = ( type: AgentTypeEnum ) => {
    setAgentType( type );

    updateAction( {
      agent_type: type,
      agent_name: undefined,
      agent_id: undefined,
      agent_version: undefined
    } );
  };

  const onAgentSelect = (
    type: AgentTypeEnum,
    agentName: string,
    agentId?: string
  ) => {
    updateAction( {
      agent_type: type,
      agent_name: agentName,
      agent_id: agentId,
      agent_version: undefined
    } );
  };

  const onVersionSelect = ( version: number ) => {
    updateAction( { agent_version: version } );
  };

  const onContextChange = ( event: React.ChangeEvent<HTMLTextAreaElement> ) => {
    updateAction( { context_values: event.currentTarget.value } );
  };

  const onConvoKeyChange = ( event: React.ChangeEvent<HTMLSelectElement> ) => {
    updateAction( { conversation_key: event.currentTarget.value } );
  };

  const onInutKeyChange = ( event: React.ChangeEvent<HTMLSelectElement> ) => {
    updateAction( { messages_key: event.currentTarget.value } );
  };

  const onMessagesIncludeChange = ( includeAll: boolean ) => {
    updateAction( { include_full_message: includeAll } );
  };

  const renderInputVariables = () => {
    return Object.keys( inputs ).map( ( key ) => (
      <option key={ key } value={ key }>
        {key}
      </option>
    ) );
  };

  const CodedAgentSelector = () => {
    const aidpAgents =
      agents?.filter( ( a ) => {
        return a.type === AgentTypeEnum.AIDP && a.id !== 'lowcode-agent';
      } ) || [];

    return (
      <Menu
        data-testid="agent-name-select"
        label="Agent Name"
        variant="select"
        value={ isPending ? 'Loading agents...' : action.agent_name }
        searchable={ true }
        disabled={ !editable || isPending }
      >
        {isPending ? (
          <MenuItem
            key="loading"
            value="Loading agents..."
            disabled={ true }
            data-testid="agent-name-loading"
          />
        ) : (
          aidpAgents.map( ( agent ) => (
            <MenuItem
              key={ agent.id }
              value={ agent.id }
              onClick={ () => onAgentSelect( agent.type, agent.id ) }
              data-testid={ `agent-name-option-${ agent.id }` }
            />
          ) )
        )}
      </Menu>
    );
  };

  const LowcodeAgentSelector = () => {
    const lowcodeAgents =
      agents?.filter( ( a ) => a.type === AgentTypeEnum.LOW_CODE ) || [];
    const selectedAgent = lowcodeAgents.find(
      ( a ) => a.name === action.agent_name
    );
    const versions = selectedAgent
      ? Object.keys( ( selectedAgent.data as AgentLowcode ).versions )
      : [];

    return (
      <>
        <Menu
          data-testid="agent-name-select"
          label="Agent Name"
          variant="select"
          value={ isPending ? 'Loading agents...' : action.agent_name }
          searchable={ true }
          disabled={ !editable || isPending }
        >
          {isPending ? (
            <MenuItem
              key="loading"
              value="Loading agents..."
              disabled={ true }
              data-testid="agent-name-loading"
            />
          ) : (
            lowcodeAgents.map( ( agent ) => (
              <MenuItem
                key={ agent.name }
                value={ agent.name }
                onClick={ () => {
                  const lowcodeData = agent.data as AgentLowcode;
                  onAgentSelect( agent.type, agent.name, lowcodeData.agentId );
                } }
                data-testid={ `agent-name-option-${ agent.name }` }
              />
            ) )
          )}
        </Menu>

        <Menu
          data-testid="agent-version-select"
          label="Version"
          variant="select"
          value={ action.agent_version?.toString() }
          disabled={ !editable || !action.agent_name }
        >
          {versions.map( ( v ) => (
            <MenuItem
              key={ v }
              value={ v }
              onClick={ () => onVersionSelect( parseInt( v ) ) }
              data-testid={ `agent-version-option-${ v }` }
            />
          ) )}
        </Menu>
      </>
    );
  };

  // Create variables array for InputWithTemplateVars
  const inputVariables: TemplateVarItem[] = Object.keys( inputs ).map( key => ( {
    id: key,
    name: key,
    type: 'Inputs'
  } ) );

  const globalContextVariables: TemplateVarItem[] = Object.keys( globalContext ).map( key => ( {
    id: `global.${ key }`,
    name: `global.${ key }`,
    type: 'Global Context'
  } ) );

  const variables = [ ...inputVariables, ...globalContextVariables ];

  return (
    <div className="configPane">
      <Text as="div" className="paneTitle" size="md" weight="bold">
        {getNodeTypeLabel( AgentConfigStepTypeEnum.AGENT )} Action
      </Text>

      <OptionRow<AgentTypeEnum>
        disabled={ !editable }
        onSelect={ onTypeChange }
        options={ AGENT_TYPE_OPTIONS }
        title="Agent Type"
        value={ agentType }
      />

      {agentType === AgentTypeEnum.LOW_CODE ? (
        <LowcodeAgentSelector />
      ) : (
        <CodedAgentSelector />
      )}

      <Text as="label" size="sm" htmlFor="mapConversation">
        Map Conversation
      </Text>
      <select
        data-testid="config-edit-panel-action-agent-map-conversation-select"
        disabled={ !editable }
        id="mapConversation"
        onChange={ onConvoKeyChange }
        value={ action.conversation_key }
      >
        <option value="none">Do Not Include</option>
        {renderInputVariables()}
      </select>
      <Text as="label" size="sm" htmlFor="conversationInput">
        Conversation Input
      </Text>
      <select
        data-testid="config-edit-panel-action-agent-conversation-input-select"
        disabled={ !editable }
        id="conversationInput"
        onChange={ onInutKeyChange }
        value={ action.messages_key }
      >
        <option value="none">Do Not Include</option>
        {renderInputVariables()}
      </select>
      <Menu
        data-testid="messages-to-include-select"
        label="Messages to Include"
        variant="select"
        value={
          action.include_full_message === true
            ? MESSAGES_INCLUDE_OPTIONS.ALL
            : action.include_full_message === false
              ? MESSAGES_INCLUDE_OPTIONS.LAST
              : MESSAGES_INCLUDE_OPTIONS.PLACEHOLDER
        }
        disabled={ !editable }
      >
        <MenuItem
          key="all"
          value={ MESSAGES_INCLUDE_OPTIONS.ALL }
          onClick={ () => onMessagesIncludeChange( true ) }
          data-testid="messages-to-include-option-all"
        />
        <MenuItem
          key="last"
          value={ MESSAGES_INCLUDE_OPTIONS.LAST }
          onClick={ () => onMessagesIncludeChange( false ) }
          data-testid="messages-to-include-option-last"
        />
      </Menu>

      <Text as="label" size="sm" htmlFor="contextValues">
        Context Values
        <Tooltip
          content={
            ( <>
              This field lets you pass additional context to the agent.
              <br />
              Add the keys and values your agent expects at runtime, in the
              expected format.
            </> )
          }
        >
          <Icon
            icon={ AiOutlineInfoCircle }
            style={ { fontSize: '12px', marginLeft: '4px', cursor: 'help' } }
          />
        </Tooltip>
      </Text>

      <InputWithTemplateVars
        data-testid="config-edit-panel-action-agent-context-values-textarea"
        disabled={ !editable }
        error={ contextValuesError }
        id="contextValues"
        inputType="textarea"
        onChange={ ( value ) => updateAction( { context_values: value } ) }
        value={ action.context_values }
        variables={ variables }
      />

      <br />
    </div>
  );
}
